/* eslint-disable */
// Phase 421 — Purchases Module (Sales-mirror for suppliers)
// ----------------------------------------------------------------------------
// Mirrors the Sales module's structure exactly but uses suppliers instead
// of suppliers (Suppliers / Supplier Invoices / Supplier Payments /
// Supplier Statements). Backed by /api/purchases/* endpoints which wrap the
// existing supplier collections.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { openPdfInBrowser } from '../utils/pdfUtils';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash, MagnifyingGlass, FileText, Receipt, ChartLineUp, PencilSimple, Envelope, FilePdf } from '@phosphor-icons/react';
import { BankAccountCombobox } from '../components/BankAccountCombobox';
import { SupplierPaymentsPrintBar } from '../components/SupplierPaymentsPrintBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (s) => { if (!s) return '-'; const p = s.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s; };
const fmtMoney = (n) => `€${(Number(n || 0)).toFixed(2)}`;

const TABS = ['suppliers', 'invoices', 'payments', 'statement'];

// Phase 415 — `Sales` accepts an optional `embed` mode used by the Finance
// section. When embedded, the component:
//   - skips the outer `<Layout>` chrome (Finance owns that),
//   - skips its own title/subtitle and the tab strip (the parent's sidebar
//     drives tab selection),
//   - lets the parent control `activeTab` via props.
// In standalone mode (default — `/sales` route) it behaves exactly as before.
export default function Purchases({ embed = false, activeTab: activeTabProp, setActiveTab: setActiveTabProp } = {}) {
  const { t, language } = useLanguage();
  useAuth();
  const sp = new URLSearchParams(window.location.search);
  const initial = TABS.includes(sp.get('tab')) ? sp.get('tab') : 'suppliers';
  const [activeTabState, setActiveTabState] = useState(initial);
  // Controlled (Finance-embedded) vs uncontrolled (standalone /sales) mode.
  const activeTab = embed && activeTabProp ? activeTabProp : activeTabState;
  const setActiveTab = embed && setActiveTabProp ? setActiveTabProp : setActiveTabState;
  const [token] = useState(localStorage.getItem('token'));
  const axCfg = { headers: { Authorization: `Bearer ${token}` } };

  // Shared dropdowns
  const [suppliers, setSuppliers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [search, setSearch] = useState('');

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    supplier_id: '', invoice_no: '', date: new Date().toISOString().slice(0, 10),
    due_date: '', terms: '', description: '', amount_net: '', vat_rate: 19, notes: '',
    items: [{ description: '', quantity: 1, rate: '', vat_rate: 19 }],
    discount: 0,
    adjustment: 0,
    // Phase 944 — Invoice type. Cash → self-settled at creation.
    invoice_type: 'credit',
  });
  // Phase 418 — Invoice row actions (Delete confirm + Send-Email dialog)
  const [invoiceDeleteTarget, setInvoiceDeleteTarget] = useState(null);
  const [invoiceEmailTarget, setInvoiceEmailTarget] = useState(null);
  const [invoiceEmailForm, setInvoiceEmailForm] = useState({ to: '', cc: '', subject: '', message: '' });
  const [invoiceEmailSending, setInvoiceEmailSending] = useState(false);

  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    supplier_id: '', date: new Date().toISOString().slice(0, 10), amount: '',
    method: 'cash', bank_account_id: '', reference: '', notes: '',
    allocations: [],
  });
  const [paymentInvoices, setPaymentInvoices] = useState([]);

  // Statement state
  const [stmtSupplierId, setStmtSupplierId] = useState('');
  const [statement, setStatement] = useState(null);
  // Phase 451 — Per-supplier date range filter for the statement view.
  const [stmtDateFrom, setStmtDateFrom] = useState('');
  const [stmtDateTo, setStmtDateTo] = useState('');

  // Phase 433 — Supplier Create / Edit / Delete dialogs.
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    supplier_id: '', name: '', tin: '', billing_address: '', email: '',
    // Phase 458 — Opening Balance carried over from a previous bookkeeping
    // system. Stored as absolute amount + type so the user UI stays clean.
    opening_balance: '',
    opening_balance_type: 'debit',
    opening_balance_date: '',
  });
  const [supplierDeleteTarget, setSupplierDeleteTarget] = useState(null);
  const [supplierDeleteCascade, setSupplierDeleteCascade] = useState(false);

  // Phase 433 — Payment edit dialog state.
  const [paymentDeleteTarget, setPaymentDeleteTarget] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);

  // Phase 436 — Nested Supplier detail (mirror of Sales customer detail).
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [supplierSubTab, setSupplierSubTab] = useState('summary');

  // Phase 438 — Global filters for the standalone Invoices / Payments
  // index tabs (act as cross-supplier searchable indexes).
  const [invSearch, setInvSearch] = useState('');
  const [invFilterSupplier, setInvFilterSupplier] = useState('__all__');
  const [invFilterStatus, setInvFilterStatus] = useState('__all__');
  const [invFilterDateFrom, setInvFilterDateFrom] = useState('');
  const [invFilterDateTo, setInvFilterDateTo] = useState('');
  const [invFilterAmountMin, setInvFilterAmountMin] = useState('');
  const [invFilterAmountMax, setInvFilterAmountMax] = useState('');
  const [payFilterSupplier, setPayFilterSupplier] = useState('__all__');
  const [payFilterMethod, setPayFilterMethod] = useState('__all__');
  const [payFilterRef, setPayFilterRef] = useState('');
  const [payFilterDateFrom, setPayFilterDateFrom] = useState('');
  const [payFilterDateTo, setPayFilterDateTo] = useState('');
  const [payFilterAmountMin, setPayFilterAmountMin] = useState('');
  const [payFilterAmountMax, setPayFilterAmountMax] = useState('');
  const [selectedPayIds, setSelectedPayIds] = useState(new Set());
  const [selectedInvIds, setSelectedInvIds] = useState(new Set());

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/purchases/suppliers`, axCfg);
      setSuppliers(res.data || []);
    } catch (e) { toast.error('Failed to load suppliers'); }
  };
  const fetchBuildings = async () => {
    try {
      const res = await axios.get(`${API}/buildings`, axCfg);
      setBuildings(res.data || []);
    } catch { /* ignore */ }
  };
  const fetchBankAccounts = async () => {
    try {
      // Phase 453 — Pull from the single Finance bank-accounts collection so
      // the dropdown matches what's visible under Finance → Bank Accounts.
      const res = await axios.get(`${API}/bank-accounts`, axCfg);
      setBankAccounts(res.data || []);
    } catch { /* ignore */ }
  };
  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/purchases/invoices`, axCfg);
      setInvoices(res.data || []);
    } catch (e) { toast.error('Failed to load invoices'); }
  };
  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API}/purchases/payments`, axCfg);
      setPayments(res.data || []);
    } catch (e) { toast.error('Failed to load payments'); }
  };
  const fetchStatement = async (supplierId, opts = {}) => {
    if (!supplierId) { setStatement(null); return; }
    try {
      // Phase 455 — Server-side date filtering so Balance Brought Forward
      // is calculated against the FULL historical ledger (not just the
      // rows visible in the window). Falls back to component state if the
      // caller doesn't pass explicit dates.
      const df = opts.date_from !== undefined ? opts.date_from : stmtDateFrom;
      const dt = opts.date_to !== undefined ? opts.date_to : stmtDateTo;
      const params = new URLSearchParams();
      if (df) params.set('date_from', df);
      if (dt) params.set('date_to', dt);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/purchases/suppliers/${supplierId}/statement${qs}`, axCfg);
      setStatement(res.data);
    } catch (e) { toast.error('Failed to load statement'); }
  };

  useEffect(() => { fetchSuppliers(); fetchBuildings(); fetchBankAccounts(); fetchInvoices(); fetchPayments(); }, []);
  useEffect(() => { fetchStatement(stmtSupplierId); }, [stmtSupplierId, stmtDateFrom, stmtDateTo]); // eslint-disable-line

  // Phase 436 — when a supplier is opened in nested detail view, auto-load
  // their statement so the Statement tab is ready.
  useEffect(() => {
    if (selectedSupplierId) {
      fetchStatement(selectedSupplierId);
      setStmtSupplierId(selectedSupplierId);
    }
  }, [selectedSupplierId]); // eslint-disable-line

  // Persist tab in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
  }, [activeTab]);

  // ---------- Supplier Create / Edit / Delete (Phase 433) ----------
  const openSupplierCreate = () => {
    setEditingSupplier(null);
    setSupplierForm({
      supplier_id: '', name: '', tin: '', billing_address: '', email: '',
      opening_balance: '', opening_balance_type: 'debit', opening_balance_date: '',
    });
    setSupplierDialog(true);
  };
  const openSupplierEdit = (c) => {
    setEditingSupplier(c);
    // Phase 458 — Preload Opening Balance. Server stores it as signed value,
    // we surface absolute amount + type so the form stays user-friendly.
    const signed = Number(c.opening_balance || 0);
    setSupplierForm({
      supplier_id: c.supplier_id, name: c.name || '', tin: c.tin || '',
      billing_address: c.billing_address || '', email: c.email || '',
      opening_balance: signed ? Math.abs(signed).toFixed(2) : '',
      opening_balance_type: c.opening_balance_type || (signed < 0 ? 'credit' : 'debit'),
      opening_balance_date: c.opening_balance_date || '',
    });
    setSupplierDialog(true);
  };
  const saveSupplier = async () => {
    if (!supplierForm.name.trim()) { toast.error(t('nameRequired') || 'Name is required'); return; }
    try {
      const payload = {
        name: supplierForm.name.trim(),
        billing_address: supplierForm.billing_address,
        email: supplierForm.email,
        // Phase 458 — Opening Balance fields.
        opening_balance: parseFloat(supplierForm.opening_balance) || 0,
        opening_balance_type: supplierForm.opening_balance_type || 'debit',
        opening_balance_date: supplierForm.opening_balance_date || null,
      };
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${supplierForm.supplier_id}`, payload, axCfg);
      } else {
        await axios.post(`${API}/suppliers`, payload, axCfg);
      }
      toast.success(t('saved'));
      setSupplierDialog(false);
      fetchSuppliers();
      if (selectedSupplierId) fetchStatement(selectedSupplierId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openSupplierDelete = (c) => {
    setSupplierDeleteTarget(c);
    setSupplierDeleteCascade(false);
  };
  const deleteSupplier = async () => {
    if (!supplierDeleteTarget) return;
    try {
      const url = `${API}/suppliers/${supplierDeleteTarget.supplier_id}${supplierDeleteCascade ? '?cascade=true' : ''}`;
      await axios.delete(url, axCfg);
      toast.success(t('deleted') || 'Deleted');
      setSupplierDeleteTarget(null);
      fetchSuppliers(); fetchInvoices(); fetchPayments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // ---------- Invoice save / delete ----------
  const openInvoiceCreate = () => {
    setEditingInvoice(null);
    setInvoiceForm({
      supplier_id: '', invoice_no: '', date: new Date().toISOString().slice(0, 10),
      due_date: '', terms: '', description: '', amount_net: '', vat_rate: 19, notes: '',
      items: [{ description: '', quantity: 1, rate: '', vat_rate: 19 }],
      discount: 0, adjustment: 0,
    });
    setInvoiceDialog(true);
  };
  const openInvoiceEdit = (inv) => {
    const items = (inv.items && inv.items.length > 0)
      ? inv.items.map(it => ({
          description: it.description || '',
          quantity: it.quantity ?? 1,
          rate: it.rate ?? '',
          vat_rate: it.vat_rate ?? 19,
        }))
      : [{
          description: inv.description || '',
          quantity: 1,
          rate: (inv.subtotal ?? inv.net) || '',
          vat_rate: 19,
        }];
    setEditingInvoice(inv);
    setInvoiceForm({
      supplier_id: inv.supplier_id || '',
      invoice_no: inv.invoice_number || '',
      date: inv.date || new Date().toISOString().slice(0, 10),
      due_date: inv.due_date || '',
      terms: inv.terms || '',
      description: inv.description || '',
      amount_net: '',
      vat_rate: 19,
      notes: inv.notes || '',
      items,
      discount: inv.discount ?? 0,
      adjustment: inv.adjustment ?? 0,
    });
    setInvoiceDialog(true);
  };
  const saveInvoice = async () => {
    if (!invoiceForm.supplier_id) { toast.error(t('selectSupplier') || 'Select a supplier'); return; }
    const cleanItems = (invoiceForm.items || [])
      .filter(it => (it.description || '').trim() || parseFloat(it.rate) > 0)
      .map(it => ({
        description: (it.description || '').trim(),
        quantity: parseFloat(it.quantity) || 1,
        rate: parseFloat(it.rate) || 0,
        vat_rate: parseFloat(it.vat_rate) || 0,
      }));
    const subtotal = cleanItems.reduce((s, it) => s + it.quantity * it.rate, 0);
    if (!cleanItems.length || subtotal <= 0) { toast.error('Add at least one invoice item with a rate'); return; }
    try {
      const payload = {
        supplier_id: invoiceForm.supplier_id,
        invoice_number: (invoiceForm.invoice_no || '').trim() || undefined,
        date: invoiceForm.date,
        due_date: invoiceForm.due_date || null,
        terms: invoiceForm.terms || '',
        description: invoiceForm.description || cleanItems.map(i => i.description).filter(Boolean).join(' / '),
        items: cleanItems,
        discount: parseFloat(invoiceForm.discount) || 0,
        adjustment: parseFloat(invoiceForm.adjustment) || 0,
        notes: invoiceForm.notes,
        // Phase 944 — Cash invoices are self-settled on creation.
        invoice_type: invoiceForm.invoice_type === 'cash' ? 'cash' : 'credit',
      };
      if (editingInvoice) await axios.put(`${API}/purchases/invoices/${editingInvoice.invoice_id}`, payload, axCfg);
      else await axios.post(`${API}/purchases/invoices`, payload, axCfg);
      toast.success(t('saved'));
      setInvoiceDialog(false);
      fetchInvoices(); fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteInvoice = async (inv) => {
    try {
      await axios.delete(`${API}/purchases/invoices/${inv.invoice_id}`, axCfg);
      toast.success(t('deleted') || 'Deleted');
      setInvoiceDeleteTarget(null);
      fetchInvoices(); fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Phase 735.13 — Centralized PDF opener (pdfUtils blob pipeline). No
  // `?token=` query, no direct `window.open` — bypasses adblockers.
  const viewInvoicePdf = async (inv) => {
    try {
      await openPdfInBrowser(`/purchases/invoices/${inv.invoice_id}/pdf`, {});
    } catch (e) { toast.error(e?.message || 'Failed to open PDF'); }
  };

  // Phase 735.49 — Bulk / multi-invoice PDF. POSTs the ordered list of
  // currently-filtered invoice_ids to the backend and hands the returned
  // Blob directly to <PdfViewerHost/> so ad-blockers cannot block it and
  // no URL length limits apply.
  const [bulkPdfLoading, setBulkPdfLoading] = useState(false);
  const viewInvoicesBulkPdf = async (list) => {
    const rows = Array.isArray(list) ? list : [];
    if (!rows.length) {
      toast.error(t('noInvoicesToPreview') || 'No invoices to preview');
      return;
    }
    setBulkPdfLoading(true);
    try {
      const ids = rows.map(r => r.invoice_id).filter(Boolean);
      const res = await axios.post(
        `${API}/purchases/invoices/pdf-bulk`,
        { invoice_ids: ids },
        { ...axCfg, responseType: 'blob' },
      );
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      window.dispatchEvent(new CustomEvent('emergent:open-pdf', {
        detail: { blob, filename: `SupplierInvoices_${ids.length}.pdf` },
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to open PDF';
      toast.error(typeof detail === 'string' ? detail : 'Failed to open PDF');
    } finally {
      setBulkPdfLoading(false);
    }
  };

  // Phase 418 — Open the Send-Email dialog pre-filled with the supplier's
  // email and a sensible subject/body.
  const openInvoiceEmail = (inv) => {
    const supplier = suppliers.find(c => c.supplier_id === inv.supplier_id);
    setInvoiceEmailTarget(inv);
    setInvoiceEmailForm({
      to: supplier?.email || '',
      cc: '',
      subject: `Invoice ${inv.invoice_no} — ${fmtMoney(inv.total)}`,
      message: `Dear ${supplier?.name || 'Customer'},\n\nPlease find attached invoice ${inv.invoice_no} dated ${fmtDate(inv.date)} for a total of ${fmtMoney(inv.total)}.\n\nThank you.`,
    });
  };
  const sendInvoiceEmail = async () => {
    if (!invoiceEmailTarget) return;
    if (!invoiceEmailForm.to.trim()) { toast.error(t('emailRequired') || 'Recipient email is required'); return; }
    setInvoiceEmailSending(true);
    try {
      await axios.post(`${API}/send-document-email`, {
        to_email: invoiceEmailForm.to.trim(),
        cc: invoiceEmailForm.cc.trim(),
        subject: invoiceEmailForm.subject,
        message: invoiceEmailForm.message,
        pdf_url: `/api/purchases/invoices/${invoiceEmailTarget.invoice_id}/pdf`,
        filename: `Invoice_${invoiceEmailTarget.invoice_no || invoiceEmailTarget.invoice_id}.pdf`,
      }, axCfg);
      toast.success(t('emailSent') || 'Email sent');
      setInvoiceEmailTarget(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    finally { setInvoiceEmailSending(false); }
  };

  // ---------- Payment save / delete ----------
  const openPaymentCreate = async (supplierId = '') => {
    setEditingPayment(null);
    setPaymentForm({
      supplier_id: supplierId, date: new Date().toISOString().slice(0, 10), amount: '',
      method: 'cash', bank_account_id: '', reference: '', notes: '',
      allocations: [],
    });
    // Phase 452 — Load open invoices upfront when the supplier is preselected
    // so the allocation table is populated immediately.
    if (supplierId) {
      try {
        const res = await axios.get(`${API}/purchases/invoices?supplier_id=${supplierId}`, axCfg);
        setPaymentInvoices((res.data || []).filter(i => i.status !== 'paid'));
      } catch { setPaymentInvoices([]); }
    } else {
      setPaymentInvoices([]);
    }
    setPaymentDialog(true);
  };
  const openPaymentEdit = async (p) => {
    setEditingPayment(p);
    setPaymentForm({
      supplier_id: p.supplier_id || '',
      date: p.date || new Date().toISOString().slice(0, 10),
      amount: p.amount ?? '',
      method: p.method || 'cash',
      bank_account_id: p.bank_account_id || '',
      reference: p.reference || '',
      notes: p.notes || '',
      allocations: p.allocations || [],
      payment_number: p.payment_number || '',
    });
    // Phase 453 — Edit Payment must use the SAME dialog and load open
    // invoices PLUS any invoices currently allocated by this payment so
    // the user can change the split without losing existing rows.
    let openInvoices = [];
    if (p.supplier_id) {
      try {
        const res = await axios.get(`${API}/purchases/invoices?supplier_id=${p.supplier_id}`, axCfg);
        openInvoices = res.data || [];
      } catch { openInvoices = []; }
    }
    // Keep all open invoices + ensure every previously-allocated invoice
    // is also present (so paid invoices linked to this payment stay
    // editable). For paid invoices we add the allocated amount back to
    // the outstanding so the user can move it elsewhere.
    const allocatedIds = (p.allocations || []).map(a => a.invoice_id);
    const visible = openInvoices.filter(i => i.status !== 'paid');
    for (const allocId of allocatedIds) {
      if (visible.some(i => i.invoice_id === allocId)) continue;
      const inv = openInvoices.find(i => i.invoice_id === allocId);
      if (inv) {
        const myAlloc = (p.allocations || []).find(a => a.invoice_id === allocId)?.amount || 0;
        // Restore "spendable" outstanding for this row by adding my own allocation back.
        visible.push({ ...inv, paid_amount: Math.max(0, (inv.paid_amount || 0) - myAlloc) });
      }
    }
    setPaymentInvoices(visible);
    setPaymentDialog(true);
  };
  const onPaymentClientChange = async (supplierId) => {
    setPaymentForm(f => ({ ...f, supplier_id: supplierId, allocations: [] }));
    if (supplierId) {
      try {
        const res = await axios.get(`${API}/purchases/invoices?supplier_id=${supplierId}`, axCfg);
        setPaymentInvoices((res.data || []).filter(i => i.status !== 'paid'));
      } catch { setPaymentInvoices([]); }
    } else setPaymentInvoices([]);
  };
  const setAllocation = (invoiceId, amount) => {
    setPaymentForm(f => {
      const arr = (f.allocations || []).filter(a => a.invoice_id !== invoiceId);
      const v = parseFloat(amount) || 0;
      if (v > 0) arr.push({ invoice_id: invoiceId, amount: v });
      return { ...f, allocations: arr };
    });
  };
  // Phase 454 — Refresh the supplier ledger after any payment mutation so
  // the Statement view reflects the new running balance, invoice status,
  // and supplier totals without a manual reload.
  const refreshAfterPaymentChange = async (supplierId) => {
    // Always refresh the cross-supplier lists & aggregates.
    await Promise.all([fetchPayments(), fetchInvoices(), fetchSuppliers()]);
    // Refetch the statement(s) currently in view so the ledger updates live.
    const ids = new Set([selectedSupplierId, stmtSupplierId, supplierId].filter(Boolean));
    await Promise.all(Array.from(ids).map(id => fetchStatement(id)));
  };
  const savePayment = async () => {
    if (!paymentForm.supplier_id) { toast.error(t('selectSupplier') || 'Select a supplier'); return; }
    const isImported = editingPayment?.source === 'bank_payment';
    const amt = parseFloat(paymentForm.amount);
    if (!isImported) {
      if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    }
    const allocSum = (paymentForm.allocations || []).reduce((s, a) => s + a.amount, 0);
    // For imported payments the amount comes from the bank statement and
    // cannot be changed — clamp against the existing amount instead.
    const refAmount = isImported ? Number(editingPayment.amount || 0) : amt;
    if (allocSum > refAmount + 0.01) { toast.error('Allocations exceed payment amount'); return; }
    try {
      // Phase 455 — Imported payments only accept supplier_id / notes /
      // allocations on the PUT endpoint; everything else is 403'd.
      const payload = isImported
        ? {
            supplier_id: paymentForm.supplier_id,
            notes: paymentForm.notes,
            allocations: paymentForm.allocations,
          }
        : {
            supplier_id: paymentForm.supplier_id, date: paymentForm.date, amount: amt,
            method: paymentForm.method, bank_account_id: paymentForm.bank_account_id || null,
            reference: paymentForm.reference, notes: paymentForm.notes,
            allocations: paymentForm.allocations,
          };
      const previousSupplierId = editingPayment?.supplier_id;
      if (editingPayment) await axios.put(`${API}/purchases/payments/${editingPayment.payment_id}`, payload, axCfg);
      else await axios.post(`${API}/purchases/payments`, payload, axCfg);
      toast.success(t('saved'));
      setPaymentDialog(false);
      await refreshAfterPaymentChange(paymentForm.supplier_id);
      if (previousSupplierId && previousSupplierId !== paymentForm.supplier_id) {
        await fetchStatement(previousSupplierId);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deletePayment = async () => {
    if (!paymentDeleteTarget) return;
    const supplierIdOfPayment = paymentDeleteTarget.supplier_id;
    try {
      await axios.delete(`${API}/purchases/payments/${paymentDeleteTarget.payment_id}`, axCfg);
      toast.success(t('deleted'));
      setPaymentDeleteTarget(null);
      await refreshAfterPaymentChange(supplierIdOfPayment);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const filteredSuppliers = (suppliers || []).filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Phase 415 — When embedded inside Finance we render WITHOUT the outer
  // Layout chrome (Finance owns it). The inner content tree is otherwise
  // identical so the Sales feature surface stays exactly the same.
  const Container = embed ? React.Fragment : Layout;
  const containerProps = embed ? {} : {};
  return (
    <Container {...containerProps}>
      <div className={embed ? '' : 'px-6 py-6 max-w-7xl mx-auto'}>
        {!embed && (
          <>
            <h1 className="text-3xl font-bold mb-1" data-testid="purchases-page-title">{t('purchases') || 'Purchases'}</h1>
            <p className="text-zinc-500 mb-6 text-sm">{t('salesSubtitle') || 'Customers, Invoices, Payments and Statements — connected to Cleaning Management & Finance.'}</p>
          </>
        )}

        {/* Tabs — hidden in embedded mode (Finance sidebar drives selection) */}
        {!embed && (
        <div className="flex gap-1 border-b border-zinc-200 mb-6" data-testid="purchases-tabs">
          {TABS.map(k => (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              data-testid={`purchases-tab-${k}`}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${activeTab === k
                ? 'border-zinc-900 text-zinc-900 font-semibold'
                : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
            >
              {t(k) || k.charAt(0).toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
        )}

        {/* SUPPLIERS — Phase 436: nested detail view */}
        {activeTab === 'suppliers' && !selectedSupplierId && (
          <div data-testid="purchases-suppliers">
            <div className="flex items-center justify-between mb-4">
              <div className="relative w-72">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  className="pl-9 rounded-none"
                  placeholder={t('searchSuppliers') || 'Search suppliers...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="purchases-suppliers-search"
                />
              </div>
              <Button onClick={openSupplierCreate} className="rounded-none" data-testid="purchases-add-supplier">
                <Plus size={16} className="mr-1" /> {t('addSupplier') || 'Add Supplier'}
              </Button>
            </div>
            <div className="bg-white border border-zinc-200">
              <table className="w-full text-sm">
                <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                  <th className="text-left px-4 py-2.5">{t('name') || 'Name'}</th>
                  <th className="text-left px-4 py-2.5">{t('email') || 'Email'}</th>
                  <th className="text-left px-4 py-2.5">{t('telephone') || t('phone') || 'Telephone'}</th>
                  <th className="text-right px-4 py-2.5">{t('totalInvoices') || t('invoices') || 'Total Invoices'}</th>
                  <th className="text-right px-4 py-2.5">{t('totalPayments') || t('payments') || 'Total Payments'}</th>
                  <th className="text-right px-4 py-2.5">{t('balance') || 'Balance'}</th>
                </tr></thead>
                <tbody>
                  {filteredSuppliers.map(c => (
                    <tr
                      key={c.supplier_id}
                      className="border-b border-zinc-100 hover:bg-zinc-50/50 cursor-pointer"
                      onClick={() => { setSelectedSupplierId(c.supplier_id); setSupplierSubTab('summary'); }}
                      data-testid={`purchases-supplier-row-${c.supplier_id}`}
                    >
                      <td className="px-4 py-2.5 font-medium text-zinc-900 hover:text-blue-700">{c.name}</td>
                      <td className="px-4 py-2.5 text-zinc-600 text-xs">{c.email || '-'}</td>
                      <td className="px-4 py-2.5 text-zinc-600 font-mono text-xs">{c.phone || '-'}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtMoney(c.total_invoiced || c.total_invoices || 0)}<span className="text-[10px] text-zinc-400 ml-1">({c.invoice_count || 0})</span></td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(c.total_paid || c.total_payments || 0)}<span className="text-[10px] text-zinc-400 ml-1">({c.payment_count || 0})</span></td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${c.balance > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmtMoney(c.balance)}</td>
                    </tr>
                  ))}
                  {filteredSuppliers.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-12 text-zinc-400">{t('noData') || 'No suppliers'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SUPPLIER DETAIL — Phase 436 */}
        {activeTab === 'suppliers' && selectedSupplierId && (() => {
          const sup = suppliers.find(c => c.supplier_id === selectedSupplierId);
          if (!sup) {
            return (
              <div className="p-12 text-center" data-testid="purchases-supplier-detail-missing">
                <p className="text-sm text-zinc-500">{t('supplierNotFound') || 'Supplier not found'}.</p>
                <button onClick={() => setSelectedSupplierId(null)} className="mt-3 text-xs text-blue-700 hover:underline">← {t('back') || 'Back'}</button>
              </div>
            );
          }
          const supplierInvoices = invoices.filter(i => i.supplier_id === selectedSupplierId);
          const supplierPayments = payments.filter(p => p.supplier_id === selectedSupplierId);
          const subTabs = [
            { key: 'summary', label: t('summary') || 'Summary' },
            { key: 'invoices', label: `${t('invoices') || 'Invoices'} (${supplierInvoices.length})` },
            { key: 'payments', label: `${t('payments') || 'Payments'} (${supplierPayments.length})` },
            { key: 'statement', label: t('statement') || 'Statement' },
          ];
          return (
            <div data-testid="purchases-supplier-detail">
              {/* Header */}
              <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                  <button
                    onClick={() => { setSelectedSupplierId(null); setSupplierSubTab('summary'); }}
                    className="text-xs text-zinc-500 hover:text-zinc-900 mb-1 flex items-center gap-1"
                    data-testid="purchases-supplier-detail-back"
                  >← {t('backToSuppliers') || 'Back to Suppliers'}</button>
                  <h2 className="text-2xl font-bold tracking-tight" data-testid="purchases-supplier-detail-name">{sup.name}</h2>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                    {sup.email && <span>{sup.email}</span>}
                    {sup.phone && <span className="font-mono">{sup.phone}</span>}
                    {sup.tin && <span className="font-mono">TIN: {sup.tin}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => openSupplierEdit(sup)} variant="outline" className="rounded-none h-8 text-xs" data-testid="purchases-supplier-detail-edit"><PencilSimple size={12} className="mr-1" />{t('edit') || 'Edit'}</Button>
                  <Button
                    onClick={() => {
                      openInvoiceCreate();
                      setInvoiceForm(f => ({ ...f, supplier_id: selectedSupplierId }));
                    }}
                    className="rounded-none bg-zinc-950 h-8 text-xs"
                    data-testid="purchases-supplier-detail-new-invoice"
                  ><Plus size={12} className="mr-1" />{t('newInvoice') || 'New Invoice'}</Button>
                  <Button
                    onClick={() => openPaymentCreate(selectedSupplierId)}
                    className="rounded-none bg-emerald-700 h-8 text-xs"
                    data-testid="purchases-supplier-detail-new-payment"
                  ><Plus size={12} className="mr-1" />{t('newPayment') || 'New Payment'}</Button>
                </div>
              </div>

              {/* KPI strip — Phase 456: Statement is the SINGLE SOURCE OF
                  TRUTH for balance figures. When the statement is loaded
                  for this supplier, we always read totals from it so the
                  header card cannot drift from the running ledger.
                  Falls back to the cross-supplier list values only while
                  the statement is still loading. */}
              {(() => {
                const stmtLoaded = !!statement && statement?.supplier?.supplier_id === sup.supplier_id;
                const totalInvoicedVal = stmtLoaded ? Number(statement.total_invoiced || 0) : Number(sup.total_invoiced || sup.total_invoices || 0);
                const totalPaidVal = stmtLoaded ? Number(statement.total_paid ?? statement.total_payments ?? 0) : Number(sup.total_paid || sup.total_payments || 0);
                const balanceVal = stmtLoaded ? Number(statement.balance || 0) : Number(sup.balance || 0);
                return (
              <div className="grid grid-cols-4 gap-3 mb-4" data-testid="purchases-supplier-detail-kpis">
                <div className="bg-white border border-zinc-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('totalInvoices') || 'Total Invoices'}</p>
                  <p className="text-lg font-mono font-bold mt-1" data-testid="purchases-supplier-detail-total-invoiced">{fmtMoney(totalInvoicedVal)}</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{sup.invoice_count || 0} {(sup.invoice_count || 0) === 1 ? t('invoice') : t('invoices')}</p>
                </div>
                <div className="bg-white border border-emerald-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">{t('totalPayments') || 'Total Payments'}</p>
                  <p className="text-lg font-mono font-bold text-emerald-700 mt-1" data-testid="purchases-supplier-detail-total-paid">{fmtMoney(totalPaidVal)}</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{sup.payment_count || 0} {(sup.payment_count || 0) === 1 ? t('payment') : t('payments')}</p>
                </div>
                <div className={`bg-white border p-3 ${balanceVal > 0 ? 'border-red-200' : 'border-emerald-200'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('balance') || 'Balance'}</p>
                  <p className={`text-lg font-mono font-bold mt-1 ${balanceVal > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="purchases-supplier-detail-balance">{fmtMoney(balanceVal)}</p>
                </div>
                <div className="bg-white border border-zinc-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('contact') || 'Contact'}</p>
                  <p className="text-xs text-zinc-700 mt-1 truncate" title={sup.email || ''}>{sup.email || '—'}</p>
                  <p className="text-xs text-zinc-700 font-mono">{sup.phone || '—'}</p>
                </div>
              </div>
                );
              })()}

              {/* Inner tabs */}
              <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="purchases-supplier-subtabs">
                {subTabs.map(st => (
                  <button
                    key={st.key}
                    onClick={() => setSupplierSubTab(st.key)}
                    data-testid={`purchases-supplier-subtab-${st.key}`}
                    className={`px-4 py-2 text-sm border-b-2 transition-colors ${supplierSubTab === st.key
                      ? 'border-zinc-900 text-zinc-900 font-semibold'
                      : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
                  >{st.label}</button>
                ))}
              </div>

              {/* SUMMARY */}
              {supplierSubTab === 'summary' && (
                <div className="space-y-4" data-testid="purchases-supplier-summary">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white border border-zinc-200 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{t('billingDetails') || 'Billing Details'}</p>
                      <div className="space-y-1 text-xs">
                        <div><span className="text-zinc-500">{t('name') || 'Name'}:</span> <span className="text-zinc-900">{sup.name}</span></div>
                        <div><span className="text-zinc-500">TIN:</span> <span className="font-mono text-zinc-900">{sup.tin || '—'}</span></div>
                        <div><span className="text-zinc-500">{t('email') || 'Email'}:</span> <span className="text-zinc-900">{sup.email || '—'}</span></div>
                        <div><span className="text-zinc-500">{t('telephone') || 'Telephone'}:</span> <span className="font-mono text-zinc-900">{sup.phone || '—'}</span></div>
                        <div><span className="text-zinc-500">{t('billingAddress') || 'Billing address'}:</span> <span className="text-zinc-900">{sup.billing_address || '—'}</span></div>
                      </div>
                    </div>
                    <div className="bg-white border border-zinc-200 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{t('recentActivity') || 'Recent Activity'}</p>
                      {supplierInvoices.length === 0 && supplierPayments.length === 0 ? (
                        <p className="text-xs text-zinc-400">{t('noActivity') || 'No activity yet.'}</p>
                      ) : (
                        <ul className="text-xs space-y-1.5">
                          {[
                            ...supplierInvoices.map(i => ({ type: 'invoice', date: i.date, ref: i.invoice_number || i.invoice_no, amount: i.grand_total ?? i.total })),
                            ...supplierPayments.map(p => ({ type: 'payment', date: p.date, ref: p.reference || (p.payment_id ? p.payment_id.slice(0, 8) : ''), amount: p.amount })),
                          ]
                            .sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.ref || '').localeCompare(a.ref || ''))
                            .slice(0, 8)
                            .map((row, i) => (
                              <li key={i} className="flex items-center justify-between border-b border-zinc-100 pb-1">
                                <span className="text-zinc-500 font-mono">{fmtDate(row.date)}</span>
                                <span className="text-zinc-700 truncate flex-1 mx-2">{row.type === 'invoice' ? `📄 ${row.ref}` : `💳 ${row.ref}`}</span>
                                <span className={`font-mono font-semibold ${row.type === 'invoice' ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(row.amount)}</span>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* INVOICES */}
              {supplierSubTab === 'invoices' && (
                <div className="bg-white border border-zinc-200" data-testid="purchases-supplier-invoices">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-2.5">№</th>
                      <th className="text-left px-4 py-2.5">{t('date')}</th>
                      <th className="text-left px-4 py-2.5">{t('description')}</th>
                      <th className="text-right px-4 py-2.5">{t('vat') || 'VAT'}</th>
                      <th className="text-right px-4 py-2.5">{t('total')}</th>
                      <th className="text-right px-4 py-2.5">{t('paid')}</th>
                      <th className="text-left px-4 py-2.5">{t('status')}</th>
                      <th className="w-36 px-4 py-2.5 text-right">{t('actions') || 'Actions'}</th>
                    </tr></thead>
                    <tbody>
                      {supplierInvoices.map(inv => (
                        <tr key={inv.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`purchases-supplier-inv-row-${inv.invoice_id}`}>
                          <td className="px-4 py-2.5 font-mono text-xs font-semibold">{inv.invoice_number || inv.invoice_no}</td>
                          <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(inv.date)}</td>
                          <td className="px-4 py-2.5 text-zinc-600 max-w-[260px] truncate">{inv.description}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtMoney(inv.vat_total ?? inv.vat ?? 0)}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmtMoney(inv.grand_total ?? inv.total)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(inv.paid_amount)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-[10px] px-1.5 py-0.5 uppercase tracking-wider border ${
                              inv.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                              inv.status === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                              'border-zinc-200 bg-zinc-50 text-zinc-600'
                            }`}>{inv.status === 'open' ? 'unpaid' : inv.status}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-end gap-0.5">
                              <button onClick={() => viewInvoicePdf(inv)} className="p-1.5 text-zinc-500 hover:text-blue-700 hover:bg-blue-50 rounded-sm" title={t('viewPdf') || 'View PDF'} data-testid={`purchases-supplier-inv-pdf-${inv.invoice_id}`}><FilePdf size={14} /></button>
                              <button onClick={() => openInvoiceEdit(inv)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm" title={t('edit') || 'Edit'} data-testid={`purchases-supplier-inv-edit-${inv.invoice_id}`}><PencilSimple size={14} /></button>
                              <button onClick={() => openInvoiceEmail(inv)} className="p-1.5 text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm" title={t('sendByEmail') || 'Send by Email'} data-testid={`purchases-supplier-inv-email-${inv.invoice_id}`}><Envelope size={14} /></button>
                              <button onClick={() => setInvoiceDeleteTarget(inv)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm" title={t('delete') || 'Delete'} data-testid={`purchases-supplier-inv-delete-${inv.invoice_id}`}><Trash size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {supplierInvoices.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-zinc-400 text-sm">{t('noInvoicesYet') || 'No invoices for this supplier yet.'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {/* PAYMENTS */}
              {supplierSubTab === 'payments' && (
                <div className="bg-white border border-zinc-200" data-testid="purchases-supplier-payments">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-2.5">{t('date')}</th>
                      <th className="text-right px-4 py-2.5">{t('amount')}</th>
                      <th className="text-left px-4 py-2.5">{t('method')}</th>
                      <th className="text-left px-4 py-2.5">{t('bankAccount')}</th>
                      <th className="text-left px-4 py-2.5">{t('reference')}</th>
                      <th className="text-left px-4 py-2.5">{t('allocated') || 'Allocated'}</th>
                      <th className="w-28 px-4 py-2.5 text-right">{t('actions') || 'Actions'}</th>
                    </tr></thead>
                    <tbody>
                      {supplierPayments.map(p => {
                        const allocSum = (p.allocations || []).reduce((s, a) => s + (a.amount || 0), 0);
                        const bank = bankAccounts.find(b => b.account_id === p.bank_account_id);
                        const isImported = p.source === 'bank_payment';
                        return (
                          <tr key={p.payment_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`purchases-supplier-pay-row-${p.payment_id}`}>
                            <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(p.date)}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmtMoney(p.amount)}</td>
                            <td className="px-4 py-2.5 text-zinc-600 capitalize">
                              <div className="flex items-center gap-1.5">
                                <span>{p.method}</span>
                                {isImported && (
                                  <span
                                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700"
                                    title={t('importedFromBankReport') || 'Imported from Bank Report'}
                                    data-testid={`purchases-supplier-pay-imported-${p.payment_id}`}
                                  >{t('imported') || 'Imported'}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-zinc-600 text-xs">{bank ? bank.account_name || bank.bank_name : '-'}</td>
                            <td className="px-4 py-2.5 font-mono text-xs">{p.reference || '-'}</td>
                            <td className="px-4 py-2.5 text-xs"><span className={allocSum >= p.amount - 0.01 ? 'text-emerald-700' : 'text-amber-700'}>{fmtMoney(allocSum)} / {fmtMoney(p.amount)}</span></td>
                            <td className="px-4 py-2.5">
                              <div className="flex justify-end gap-0.5">
                                <button onClick={() => openPaymentEdit(p)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm" title={isImported ? (t('manageAllocations') || 'Manage Allocations') : (t('edit') || 'Edit')} data-testid={`purchases-supplier-pay-edit-${p.payment_id}`}><PencilSimple size={14} /></button>
                                {!isImported && (
                                  <button onClick={() => setPaymentDeleteTarget(p)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm" title={t('delete') || 'Delete'} data-testid={`purchases-supplier-pay-delete-${p.payment_id}`}><Trash size={14} /></button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {supplierPayments.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-zinc-400 text-sm">{t('noPaymentsYet') || 'No payments for this supplier yet.'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {/* STATEMENT — Phase 451 redesign:
                  - Date range filters (From / To) that affect invoices,
                    payments, totals AND the running balance.
                  - Summary cards at the TOP: Total Invoice Amount,
                    Total Payments, Outstanding Balance.
                  - Columns: Date · Type · Reference · Description ·
                    Debit · Credit · Running Balance.
                  - Sort order: ALWAYS oldest → newest so the running
                    balance flows from zero to the closing balance. */}
              {supplierSubTab === 'statement' && (
                <div data-testid="purchases-supplier-statement">
                  {statement ? (() => {
                    // Phase 455 — Date filtering is now server-side; we
                    // just render whatever the API returned (it already
                    // prepends a Balance Brought Forward row + filtered
                    // entries when date_from is set).
                    const allEntries = statement.entries || statement.timeline || [];
                    const sorted = [...allEntries].sort((a, b) => {
                      // Opening Balance ALWAYS sorts first, then Brought-forward.
                      if (a.is_opening_balance) return -1;
                      if (b.is_opening_balance) return 1;
                      if (a.is_brought_forward) return -1;
                      if (b.is_brought_forward) return 1;
                      const cmp = (a.date || '').localeCompare(b.date || '');
                      if (cmp !== 0) return cmp;
                      // Invoice rows before payment rows on same date so running balance starts up then comes down.
                      const ka = a.kind || a.type || '';
                      const kb = b.kind || b.type || '';
                      if (ka === 'invoice' && kb !== 'invoice') return -1;
                      if (kb === 'invoice' && ka !== 'invoice') return 1;
                      return (a.created_at || '').localeCompare(b.created_at || '');
                    });
                    const broughtForward = Number(statement.brought_forward || 0);
                    let run = broughtForward;
                    const ledger = sorted.map(e => {
                      if (e.is_opening_balance) {
                        const obDebit = Number(e.debit || 0);
                        const obCredit = Number(e.credit || 0);
                        run = Math.round((run + obDebit - obCredit) * 100) / 100;
                        return { ...e, _type: 'opening_balance', _debit: obDebit, _credit: obCredit, _running: run };
                      }
                      if (e.is_brought_forward) {
                        return { ...e, _type: 'brought_forward', _debit: 0, _credit: 0, _running: broughtForward };
                      }
                      const isInvoice = (e.type === 'invoice' || e.kind === 'invoice');
                      const debit = isInvoice ? Number(e.amount ?? e.debit ?? 0) : 0;
                      const credit = !isInvoice ? Number(e.amount ?? e.credit ?? 0) : 0;
                      run = Math.round((run + debit - credit) * 100) / 100;
                      return { ...e, _type: isInvoice ? 'invoice' : 'payment', _debit: debit, _credit: credit, _running: run };
                    });
                    const totalInvoiced = Number(statement.total_invoiced || 0);
                    const totalPayments = Number(statement.total_payments || statement.total_paid || 0);
                    const outstanding = Number(statement.balance || 0);
                    const hasFilter = !!(stmtDateFrom || stmtDateTo);
                    return (
                      <>
                        {/* Summary cards at top — values come from server with brought-forward applied */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="supplier-stmt-summary">
                          <div className="bg-white border border-zinc-200 p-4" data-testid="stmt-total-invoiced">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t('totalInvoiceAmount') || t('totalInvoiced') || 'Total Invoice Amount'}</p>
                            <p className="font-mono text-xl font-bold mt-1">{fmtMoney(totalInvoiced)}</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5">{ledger.filter(r => r._type === 'invoice').length} {t('invoicesLabel') || 'invoices'}</p>
                          </div>
                          <div className="bg-white border border-emerald-200 p-4" data-testid="stmt-total-payments">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{t('totalPayments') || 'Total Payments'}</p>
                            <p className="font-mono text-xl font-bold text-emerald-700 mt-1">{fmtMoney(totalPayments)}</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5">{ledger.filter(r => r._type === 'payment').length} {t('paymentsLabel') || 'payments'}</p>
                          </div>
                          <div className={`bg-white border p-4 ${outstanding > 0 ? 'border-red-200' : 'border-emerald-200'}`} data-testid="stmt-outstanding">
                            <p className={`text-[10px] font-bold uppercase tracking-wider ${outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{t('outstandingBalance') || 'Outstanding Balance'}</p>
                            <p className={`font-mono text-xl font-bold mt-1 ${outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(outstanding)}</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5">{outstanding > 0 ? (t('weOwe') || 'We owe') : outstanding < 0 ? (t('credit') || 'Credit') : (t('settled') || 'Settled')}</p>
                          </div>
                        </div>

                        {/* Date filters */}
                        <div className="flex items-end justify-end mb-3 gap-2 flex-wrap" data-testid="supplier-stmt-filters">
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500 block mb-1">{t('fromDate') || 'From'}</Label>
                            <Input type="date" value={stmtDateFrom} onChange={e => setStmtDateFrom(e.target.value)} className="rounded-none h-8 w-44 min-w-[160px] shrink-0 font-mono text-xs" data-testid="supplier-stmt-from" />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500 block mb-1">{t('toDate') || 'To'}</Label>
                            <Input type="date" value={stmtDateTo} onChange={e => setStmtDateTo(e.target.value)} className="rounded-none h-8 w-44 min-w-[160px] shrink-0 font-mono text-xs" data-testid="supplier-stmt-to" />
                          </div>
                          {hasFilter && (
                            <Button variant="outline" size="sm" onClick={() => { setStmtDateFrom(''); setStmtDateTo(''); }} className="rounded-none h-8 text-xs" data-testid="supplier-stmt-clear">
                              {t('clearFilter') || 'Clear'}
                            </Button>
                          )}
                        </div>

                        {/* Ledger table */}
                        <div className="bg-white border border-zinc-200 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                                <th className="text-left px-4 py-2.5">{t('date') || 'Date'}</th>
                                <th className="text-left px-4 py-2.5">{t('type') || 'Type'}</th>
                                <th className="text-left px-4 py-2.5">{t('reference') || 'Reference'}</th>
                                <th className="text-left px-4 py-2.5">{t('description') || 'Description'}</th>
                                <th className="text-right px-4 py-2.5">{t('debit') || 'Debit'} (€)</th>
                                <th className="text-right px-4 py-2.5">{t('credit') || 'Credit'} (€)</th>
                                <th className="text-right px-4 py-2.5">{t('runningBalance') || 'Running Balance'} (€)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ledger.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-12 text-zinc-400">{hasFilter ? (t('noEntriesInRange') || 'No entries in the selected date range.') : (t('noData') || 'No entries')}</td></tr>
                              ) : (
                                <>
                                  {ledger.map((r, i) => {
                                    if (r._type === 'opening_balance') {
                                      return (
                                        <tr key={`ob-${i}`} className="bg-blue-50 border-b border-blue-200 font-semibold" data-testid="supplier-stmt-opening-balance">
                                          <td className="px-4 py-2 font-mono text-xs">{r.date ? fmtDate(r.date) : '—'}</td>
                                          <td className="px-4 py-2">
                                            <span className="px-1.5 py-0.5 text-[10px] border border-blue-300 bg-blue-100 text-blue-800 uppercase tracking-wider font-mono">{t('openingBalance') || 'Opening Balance'}</span>
                                          </td>
                                          <td className="px-4 py-2 font-mono text-xs">OB</td>
                                          <td className="px-4 py-2 text-zinc-700 italic">{t('openingBalance') || 'Opening Balance'} {r.opening_balance_type === 'credit' ? `(${t('credit') || 'Credit'})` : ''}</td>
                                          <td className="px-4 py-2 text-right font-mono text-red-700">{r._debit > 0 ? fmtMoney(r._debit) : ''}</td>
                                          <td className="px-4 py-2 text-right font-mono text-emerald-700">{r._credit > 0 ? fmtMoney(r._credit) : ''}</td>
                                          <td className={`px-4 py-2 text-right font-mono font-bold ${r._running > 0 ? 'text-red-700' : r._running < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>{fmtMoney(r._running)}</td>
                                        </tr>
                                      );
                                    }
                                    if (r._type === 'brought_forward') {
                                      return (
                                        <tr key={`bf-${i}`} className="bg-amber-50 border-b border-amber-200 font-semibold" data-testid="supplier-stmt-brought-forward">
                                          <td className="px-4 py-2 font-mono text-xs">{fmtDate(r.date)}</td>
                                          <td className="px-4 py-2">
                                            <span className="px-1.5 py-0.5 text-[10px] border border-amber-300 bg-amber-100 text-amber-800 uppercase tracking-wider font-mono">{t('broughtForward') || 'B/F'}</span>
                                          </td>
                                          <td className="px-4 py-2 font-mono text-xs">—</td>
                                          <td className="px-4 py-2 text-zinc-700 italic">{t('balanceBroughtForward') || 'Balance Brought Forward'}</td>
                                          <td className="px-4 py-2"></td>
                                          <td className="px-4 py-2"></td>
                                          <td className={`px-4 py-2 text-right font-mono font-bold ${r._running > 0 ? 'text-red-700' : r._running < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>{fmtMoney(r._running)}</td>
                                        </tr>
                                      );
                                    }
                                    const rowIsImported = r.source === 'bank_payment';
                                    return (
                                      <tr key={r.payment_id || r.bill_id || i} className="border-b border-zinc-100" data-testid={`supplier-stmt-row-${i}`}>
                                        <td className="px-4 py-2 font-mono text-xs">{fmtDate(r.date)}</td>
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-1.5">
                                            <span className={`px-1.5 py-0.5 text-[10px] border uppercase tracking-wider font-mono ${r._type === 'invoice' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                                              {r._type === 'invoice' ? (t('invoice') || 'Invoice') : (t('payment') || 'Payment')}
                                            </span>
                                            {rowIsImported && (
                                              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700" title={t('importedFromBankReport') || 'Imported from Bank Report'}>{t('imported') || 'Imp.'}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs">{r.reference || r.ref || '—'}</td>
                                        <td className="px-4 py-2 text-zinc-700">{r.description || ''}</td>
                                        <td className="px-4 py-2 text-right font-mono text-red-700">{r._debit > 0 ? fmtMoney(r._debit) : ''}</td>
                                        <td className="px-4 py-2 text-right font-mono text-emerald-700">{r._credit > 0 ? fmtMoney(r._credit) : ''}</td>
                                        <td className={`px-4 py-2 text-right font-mono font-semibold ${r._running > 0 ? 'text-red-700' : r._running < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>{fmtMoney(r._running)}</td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300">
                                    <td colSpan={4} className="px-4 py-2 text-right">{t('totals') || 'Totals'}</td>
                                    <td className="px-4 py-2 text-right font-mono text-red-700">{fmtMoney(totalInvoiced)}</td>
                                    <td className="px-4 py-2 text-right font-mono text-emerald-700">{fmtMoney(totalPayments)}</td>
                                    <td className={`px-4 py-2 text-right font-mono font-bold ${outstanding > 0 ? 'text-red-700' : 'text-zinc-900'}`}>{fmtMoney(outstanding)}</td>
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })() : (
                    <p className="text-zinc-400 text-sm">{t('loading') || 'Loading…'}</p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* INVOICES — Phase 438 global searchable index with filters */}
        {activeTab === 'invoices' && (() => {
          const minA = parseFloat(invFilterAmountMin);
          const maxA = parseFloat(invFilterAmountMax);
          const filteredInvoices = invoices.filter(i => {
            if (invSearch) {
              const q = invSearch.toLowerCase();
              const hit = (i.invoice_number || i.invoice_no || '').toLowerCase().includes(q)
                || (i.supplier_name || '').toLowerCase().includes(q)
                || (i.description || '').toLowerCase().includes(q);
              if (!hit) return false;
            }
            if (invFilterSupplier !== '__all__' && i.supplier_id !== invFilterSupplier) return false;
            // Phase 554 — Treat legacy "open" as "unpaid" so filter matches.
            if (invFilterStatus !== '__all__') {
              const s = (i.status === 'open' ? 'unpaid' : i.status);
              if (s !== invFilterStatus) return false;
            }
            if (invFilterDateFrom && (i.date || '') < invFilterDateFrom) return false;
            if (invFilterDateTo && (i.date || '') > invFilterDateTo) return false;
            const total = Number(i.grand_total ?? i.total) || 0;
            if (!isNaN(minA) && total < minA) return false;
            if (!isNaN(maxA) && total > maxA) return false;
            return true;
          });
          const invActive = (invFilterSupplier !== '__all__' ? 1 : 0)
            + (invFilterStatus !== '__all__' ? 1 : 0)
            + (invFilterDateFrom ? 1 : 0) + (invFilterDateTo ? 1 : 0)
            + (invFilterAmountMin ? 1 : 0) + (invFilterAmountMax ? 1 : 0);
          const clearInvFilters = () => {
            setInvSearch(''); setInvFilterSupplier('__all__'); setInvFilterStatus('__all__');
            setInvFilterDateFrom(''); setInvFilterDateTo('');
            setInvFilterAmountMin(''); setInvFilterAmountMax('');
          };
          return (
            <div data-testid="purchases-invoices">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">{t('invoicesLabel') || t('invoices') || 'Invoices'}</h2>
                  <span className="text-xs text-zinc-400">({filteredInvoices.length}/{invoices.length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder={t('search') ? `${t('search')} № / ${t('supplier') || 'supplier'} / ${t('description') || 'description'}…` : 'Search…'}
                    value={invSearch}
                    onChange={e => setInvSearch(e.target.value)}
                    className="rounded-none h-8 w-72 text-xs"
                    data-testid="purchases-invoice-search"
                  />
                  <Button
                    onClick={() => viewInvoicesBulkPdf(selectedInvIds.size ? invoices.filter(i => selectedInvIds.has(i.invoice_id)) : filteredInvoices)}
                    disabled={filteredInvoices.length === 0 || bulkPdfLoading}
                    variant="outline"
                    className="rounded-none h-8 gap-1"
                    data-testid="purchases-invoices-view-pdf-bulk"
                    title={t('viewPdfForFiltered') || 'Open a single PDF containing every filtered invoice'}
                  >
                    <FilePdf size={14} />
                    {bulkPdfLoading ? (t('loading') || 'Loading…') : (t('viewPdf') || 'View PDF')}
                    {filteredInvoices.length > 0 && <span className="text-[10px] text-zinc-400 font-mono ml-1">({selectedInvIds.size || filteredInvoices.length})</span>}
                  </Button>
                  <Button onClick={openInvoiceCreate} className="rounded-none bg-zinc-950 h-8" data-testid="purchases-add-invoice"><Plus size={14} className="mr-1" /> {t('newInvoice') || 'New Invoice'}</Button>
                </div>
              </div>
              <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 mb-3 flex items-end gap-2 flex-wrap" data-testid="purchases-invoice-filters">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('supplier') || 'Supplier'}</Label>
                  <Select value={invFilterSupplier} onValueChange={setInvFilterSupplier}>
                    <SelectTrigger className="rounded-none h-7 w-48 text-xs" data-testid="purchases-invoice-filter-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('status') || 'Status'}</Label>
                  <Select value={invFilterStatus} onValueChange={setInvFilterStatus}>
                    <SelectTrigger className="rounded-none h-7 w-32 text-xs" data-testid="purchases-invoice-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      <SelectItem value="unpaid">{t('unpaid') || 'Unpaid'}</SelectItem>
                      <SelectItem value="partial">{t('partial') || 'Partial'}</SelectItem>
                      <SelectItem value="paid">{t('paid') || 'Paid'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('from') || 'From'}</Label>
                  <Input type="date" value={invFilterDateFrom} onChange={e => setInvFilterDateFrom(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="purchases-invoice-filter-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('to') || 'To'}</Label>
                  <Input type="date" value={invFilterDateTo} onChange={e => setInvFilterDateTo(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="purchases-invoice-filter-to" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('minAmount') || 'Min €'}</Label>
                  <Input type="number" step="0.01" value={invFilterAmountMin} onChange={e => setInvFilterAmountMin(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="0" data-testid="purchases-invoice-filter-min" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('maxAmount') || 'Max €'}</Label>
                  <Input type="number" step="0.01" value={invFilterAmountMax} onChange={e => setInvFilterAmountMax(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="∞" data-testid="purchases-invoice-filter-max" />
                </div>
                {(invActive > 0 || invSearch) && (
                  <button type="button" onClick={clearInvFilters} className="h-7 px-3 text-xs border border-zinc-300 bg-white hover:bg-zinc-100 ml-auto flex items-center gap-1" data-testid="purchases-invoice-filter-clear">✕ {t('clearAll') || 'Clear'} {invActive > 0 && <span className="text-zinc-400">({invActive})</span>}</button>
                )}
              </div>
              <SupplierPaymentsPrintBar
                kind="invoices"
                filtered={filteredInvoices}
                selected={selectedInvIds}
                setSelected={setSelectedInvIds}
                el={language === 'el'}
                meta={{
                  date_from: invFilterDateFrom, date_to: invFilterDateTo,
                  supplier_label: invFilterSupplier !== '__all__' ? (suppliers.find(s => s.supplier_id === invFilterSupplier)?.name || '') : '',
                  filters_label: [invFilterStatus !== '__all__' ? `Status: ${invFilterStatus}` : '', invSearch ? `Search: ${invSearch}` : '', invFilterAmountMin ? `Min €${invFilterAmountMin}` : '', invFilterAmountMax ? `Max €${invFilterAmountMax}` : ''].filter(Boolean).join(' · '),
                }}
              />
              <div className="bg-white border border-zinc-200 max-h-[calc(100vh-320px)] overflow-auto">
                <table className="w-full text-sm" data-testid="purchases-invoices-table">
                  <thead className="sticky top-0 z-10"><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                    <th className="w-8 px-2 py-2.5 bg-zinc-50">
                      <input type="checkbox" className="accent-zinc-900"
                        checked={filteredInvoices.length > 0 && filteredInvoices.every(i => selectedInvIds.has(i.invoice_id))}
                        onChange={e => setSelectedInvIds(prev => { const n = new Set(prev); filteredInvoices.forEach(i => (e.target.checked ? n.add(i.invoice_id) : n.delete(i.invoice_id))); return n; })}
                        data-testid="purchases-inv-select-header" />
                    </th>
                    <th className="text-left px-4 py-2.5 bg-zinc-50">№</th>
                    <th className="text-left px-4 py-2.5 bg-zinc-50">{t('date') || 'Date'}</th>
                    <th className="text-left px-4 py-2.5 bg-zinc-50">{t('supplier') || 'Supplier'}</th>
                    <th className="text-left px-4 py-2.5 bg-zinc-50">{t('description') || 'Description'}</th>
                    {/* Phase 549 — Reference # column placed right after Description.
                        For supplier invoices, falls back to inv.reference_no
                        when a dedicated external reference is stored. */}
                    <th className="text-left px-4 py-2.5 bg-zinc-50">{t('referenceNo') || 'Reference #'}</th>
                    {/* Phase 735.48 — Net column (Net = Total − VAT). */}
                    <th className="text-right px-4 py-2.5 bg-zinc-50">{t('net') || 'Net'}</th>
                    <th className="text-right px-4 py-2.5 bg-zinc-50">{t('vat') || 'VAT'}</th>
                    <th className="text-right px-4 py-2.5 bg-zinc-50">{t('total') || 'Total'}</th>
                    <th className="text-right px-4 py-2.5 bg-zinc-50">{t('paid') || 'Paid'}</th>
                    <th className="text-left px-4 py-2.5 bg-zinc-50">{t('status') || 'Status'}</th>
                    <th className="w-32 px-4 py-2.5 text-right bg-zinc-50">{t('actions') || 'Actions'}</th>
                  </tr></thead>
                  <tbody>
                    {filteredInvoices.map(inv => {
                      const invTotal = Number(inv.grand_total ?? inv.total) || 0;
                      const invVat = Number(inv.vat_total ?? inv.vat ?? 0) || 0;
                      const invNet = Math.round((invTotal - invVat) * 100) / 100;
                      return (
                      <tr key={inv.invoice_id} className={`border-b border-zinc-100 hover:bg-zinc-50/50 ${selectedInvIds.has(inv.invoice_id) ? 'bg-amber-50/60' : ''}`} data-testid={`purchases-inv-row-${inv.invoice_id}`}>
                        <td className="px-2 py-2.5 text-center">
                          <input type="checkbox" className="accent-zinc-900" checked={selectedInvIds.has(inv.invoice_id)}
                            onChange={() => setSelectedInvIds(prev => { const n = new Set(prev); if (n.has(inv.invoice_id)) n.delete(inv.invoice_id); else n.add(inv.invoice_id); return n; })}
                            data-testid={`purchases-inv-select-${inv.invoice_id}`} />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs font-semibold">{inv.invoice_number || inv.invoice_no}</td>
                        <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(inv.date)}</td>
                        <td className="px-4 py-2.5">{inv.supplier_name}</td>
                        <td className="px-4 py-2.5 text-zinc-600 max-w-[260px] truncate">{inv.description}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-zinc-600" data-testid={`purchases-inv-ref-${inv.invoice_id}`}>{inv.reference_no || '—'}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs" data-testid={`purchases-inv-net-${inv.invoice_id}`}>{fmtMoney(invNet)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtMoney(invVat)}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmtMoney(invTotal)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(inv.paid_amount)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[10px] px-1.5 py-0.5 uppercase tracking-wider border ${
                            inv.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                            inv.status === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                            'border-zinc-200 bg-zinc-50 text-zinc-600'
                          }`}>{inv.status === 'open' ? 'unpaid' : inv.status}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1" data-testid={`purchases-inv-actions-${inv.invoice_id}`}>
                            <button onClick={() => openInvoiceEdit(inv)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm transition-colors" title={t('edit') || 'Edit'} data-testid={`purchases-inv-edit-${inv.invoice_id}`}><PencilSimple size={15} /></button>
                            <button onClick={() => openInvoiceEmail(inv)} className="p-1.5 text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm transition-colors" title={t('sendByEmail') || 'Send by Email'} data-testid={`purchases-inv-email-${inv.invoice_id}`}><Envelope size={15} /></button>
                            <button onClick={() => setInvoiceDeleteTarget(inv)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" title={t('delete') || 'Delete'} data-testid={`purchases-inv-delete-${inv.invoice_id}`}><Trash size={15} /></button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                    {filteredInvoices.length === 0 && <tr><td colSpan={12} className="text-center py-12 text-zinc-400">{t('noData') || 'No invoices'}</td></tr>}
                  </tbody>
                  {/* Phase 735.48 — Sticky footer totals across the entire filtered dataset. */}
                  {filteredInvoices.length > 0 && (() => {
                    const totals = filteredInvoices.reduce((acc, inv) => {
                      const t_ = Number(inv.grand_total ?? inv.total) || 0;
                      const v_ = Number(inv.vat_total ?? inv.vat ?? 0) || 0;
                      acc.grand += t_;
                      acc.vat += v_;
                      acc.net += (t_ - v_);
                      return acc;
                    }, { net: 0, vat: 0, grand: 0 });
                    const r2 = (n) => Math.round(n * 100) / 100;
                    return (
                      <tfoot className="sticky bottom-0 z-10" data-testid="purchases-inv-footer-totals">
                        <tr className="bg-zinc-100 border-t-2 border-zinc-300 font-semibold">
                          <td colSpan={6} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-zinc-600 bg-zinc-100">
                            {t('totals') || 'Totals'} <span className="text-zinc-400 font-normal">({filteredInvoices.length} {filteredInvoices.length === 1 ? (t('invoice') || 'invoice') : (t('invoices') || 'invoices')})</span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-sm bg-zinc-100" data-testid="purchases-inv-footer-net">{fmtMoney(r2(totals.net))}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-sm bg-zinc-100" data-testid="purchases-inv-footer-vat">{fmtMoney(r2(totals.vat))}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-sm font-bold text-red-800 bg-zinc-100" data-testid="purchases-inv-footer-grand">{fmtMoney(r2(totals.grand))}</td>
                          <td colSpan={3} className="bg-zinc-100"></td>
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            </div>
          );
        })()}

        {/* PAYMENTS — Phase 438 global filters */}
        {activeTab === 'payments' && (() => {
          const minA = parseFloat(payFilterAmountMin);
          const maxA = parseFloat(payFilterAmountMax);
          const filteredPayments = payments.filter(p => {
            if (payFilterSupplier !== '__all__' && p.supplier_id !== payFilterSupplier) return false;
            if (payFilterMethod !== '__all__' && p.method !== payFilterMethod) return false;
            if (payFilterRef) {
              const q = payFilterRef.toLowerCase();
              const hit = (p.reference || '').toLowerCase().includes(q)
                || (p.notes || '').toLowerCase().includes(q)
                || (p.supplier_name || '').toLowerCase().includes(q);
              if (!hit) return false;
            }
            if (payFilterDateFrom && (p.date || '') < payFilterDateFrom) return false;
            if (payFilterDateTo && (p.date || '') > payFilterDateTo) return false;
            const amt = Number(p.amount) || 0;
            if (!isNaN(minA) && amt < minA) return false;
            if (!isNaN(maxA) && amt > maxA) return false;
            return true;
          });
          const payActive = (payFilterSupplier !== '__all__' ? 1 : 0)
            + (payFilterMethod !== '__all__' ? 1 : 0)
            + (payFilterRef ? 1 : 0)
            + (payFilterDateFrom ? 1 : 0) + (payFilterDateTo ? 1 : 0)
            + (payFilterAmountMin ? 1 : 0) + (payFilterAmountMax ? 1 : 0);
          const clearPayFilters = () => {
            setPayFilterSupplier('__all__'); setPayFilterMethod('__all__');
            setPayFilterRef(''); setPayFilterDateFrom(''); setPayFilterDateTo('');
            setPayFilterAmountMin(''); setPayFilterAmountMax('');
          };
          return (
            <div data-testid="purchases-payments">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">{t('paymentsLabel') || t('payments') || 'Payments'}</h2>
                  <span className="text-xs text-zinc-400">({filteredPayments.length}/{payments.length})</span>
                </div>
                <Button onClick={() => openPaymentCreate()} className="rounded-none bg-zinc-950 h-8" data-testid="purchases-add-payment"><Plus size={14} className="mr-1" /> {t('newPayment') || 'New Payment'}</Button>
              </div>
              <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 mb-3 flex items-end gap-2 flex-wrap" data-testid="purchases-payment-filters">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('supplier') || 'Supplier'}</Label>
                  <Select value={payFilterSupplier} onValueChange={setPayFilterSupplier}>
                    <SelectTrigger className="rounded-none h-7 w-48 text-xs" data-testid="purchases-payment-filter-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('method') || 'Method'}</Label>
                  <Select value={payFilterMethod} onValueChange={setPayFilterMethod}>
                    <SelectTrigger className="rounded-none h-7 w-28 text-xs" data-testid="purchases-payment-filter-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      <SelectItem value="cash">{t('cash') || 'Cash'}</SelectItem>
                      <SelectItem value="bank">{t('bankTransfer') || 'Bank'}</SelectItem>
                      <SelectItem value="card">{t('card') || 'Card'}</SelectItem>
                      <SelectItem value="cheque">{t('cheque') || 'Cheque'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('reference') || 'Reference'}</Label>
                  <Input type="text" value={payFilterRef} onChange={e => setPayFilterRef(e.target.value)} className="rounded-none h-7 w-44 text-xs font-mono" placeholder={t('search') ? `${t('search')}…` : 'Search…'} data-testid="purchases-payment-filter-ref" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('from') || 'From'}</Label>
                  <Input type="date" value={payFilterDateFrom} onChange={e => setPayFilterDateFrom(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="purchases-payment-filter-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('to') || 'To'}</Label>
                  <Input type="date" value={payFilterDateTo} onChange={e => setPayFilterDateTo(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="purchases-payment-filter-to" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('minAmount') || 'Min €'}</Label>
                  <Input type="number" step="0.01" value={payFilterAmountMin} onChange={e => setPayFilterAmountMin(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="0" data-testid="purchases-payment-filter-min" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('maxAmount') || 'Max €'}</Label>
                  <Input type="number" step="0.01" value={payFilterAmountMax} onChange={e => setPayFilterAmountMax(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="∞" data-testid="purchases-payment-filter-max" />
                </div>
                {payActive > 0 && (
                  <button type="button" onClick={clearPayFilters} className="h-7 px-3 text-xs border border-zinc-300 bg-white hover:bg-zinc-100 ml-auto flex items-center gap-1" data-testid="purchases-payment-filter-clear">✕ {t('clearAll') || 'Clear'} <span className="text-zinc-400">({payActive})</span></button>
                )}
              </div>
              <SupplierPaymentsPrintBar
                filtered={filteredPayments}
                selected={selectedPayIds}
                setSelected={setSelectedPayIds}
                el={language === 'el'}
                meta={{
                  date_from: payFilterDateFrom, date_to: payFilterDateTo,
                  supplier_label: payFilterSupplier !== '__all__' ? (suppliers.find(s => s.supplier_id === payFilterSupplier)?.name || '') : '',
                  filters_label: [payFilterMethod !== '__all__' ? `Method: ${payFilterMethod}` : '', payFilterRef ? `Search: ${payFilterRef}` : '', payFilterAmountMin ? `Min €${payFilterAmountMin}` : '', payFilterAmountMax ? `Max €${payFilterAmountMax}` : ''].filter(Boolean).join(' · '),
                }}
              />
              <div className="bg-white border border-zinc-200">
                <table className="w-full text-sm" data-testid="purchases-payments-table">
                  <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                    <th className="w-8 px-2 py-2.5">
                      <input type="checkbox" className="accent-zinc-900"
                        checked={filteredPayments.length > 0 && filteredPayments.every(p => selectedPayIds.has(p.payment_id))}
                        onChange={e => setSelectedPayIds(prev => { const n = new Set(prev); filteredPayments.forEach(p => (e.target.checked ? n.add(p.payment_id) : n.delete(p.payment_id))); return n; })}
                        data-testid="purchases-pay-select-header" />
                    </th>
                    <th className="text-left px-4 py-2.5">{t('date') || 'Date'}</th>
                    <th className="text-left px-4 py-2.5">{t('supplier') || 'Supplier'}</th>
                    <th className="text-right px-4 py-2.5">{t('amount') || 'Amount'}</th>
                    <th className="text-left px-4 py-2.5">{t('method') || 'Method'}</th>
                    <th className="text-left px-4 py-2.5">{t('bankAccount') || 'Bank Account'}</th>
                    <th className="text-left px-4 py-2.5">{t('reference') || 'Reference'}</th>
                    <th className="text-left px-4 py-2.5">{t('allocated') || 'Allocated'}</th>
                    <th className="w-28 px-4 py-2.5 text-right">{t('actions') || 'Actions'}</th>
                  </tr></thead>
                  <tbody>
                    {filteredPayments.map(p => {
                      const allocSum = (p.allocations || []).reduce((s, a) => s + (a.amount || 0), 0);
                      const bank = bankAccounts.find(b => b.account_id === p.bank_account_id);
                      const isImported = p.source === 'bank_payment';
                      return (
                        <tr key={p.payment_id} className={`border-b border-zinc-100 hover:bg-zinc-50/50 ${selectedPayIds.has(p.payment_id) ? 'bg-amber-50/60' : ''}`} data-testid={`purchases-pay-row-${p.payment_id}`}>
                          <td className="px-2 py-2.5 text-center">
                            <input type="checkbox" className="accent-zinc-900" checked={selectedPayIds.has(p.payment_id)}
                              onChange={() => setSelectedPayIds(prev => { const n = new Set(prev); if (n.has(p.payment_id)) n.delete(p.payment_id); else n.add(p.payment_id); return n; })}
                              data-testid={`purchases-pay-select-${p.payment_id}`} />
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(p.date)}</td>
                          <td className="px-4 py-2.5">{p.supplier_name}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmtMoney(p.amount)}</td>
                          <td className="px-4 py-2.5 text-zinc-600 capitalize">
                            <div className="flex items-center gap-1.5">
                              <span>{p.method}</span>
                              {isImported && (
                                <span
                                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700"
                                  title={t('importedFromBankReport') || 'Imported from Bank Report'}
                                  data-testid={`purchases-pay-imported-${p.payment_id}`}
                                >{t('imported') || 'Imported'}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-zinc-600 text-xs">{bank ? bank.account_name || bank.bank_name : '-'}</td>
                          <td className="px-4 py-2.5 font-mono text-xs">{p.reference || '-'}</td>
                          <td className="px-4 py-2.5 text-xs"><span className={allocSum >= p.amount - 0.01 ? 'text-emerald-700' : 'text-amber-700'}>{fmtMoney(allocSum)} / {fmtMoney(p.amount)}</span></td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end gap-0.5">
                              <button onClick={() => openPaymentEdit(p)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm" title={isImported ? (t('manageAllocations') || 'Manage Allocations') : (t('edit') || 'Edit')} data-testid={`purchases-pay-edit-${p.payment_id}`}><PencilSimple size={14} /></button>
                              {!isImported && (
                                <button onClick={() => setPaymentDeleteTarget(p)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm" title={t('delete') || 'Delete'} data-testid={`purchases-pay-delete-${p.payment_id}`}><Trash size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredPayments.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-zinc-400">{t('noData') || 'No payments'}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* STATEMENT */}
        {activeTab === 'statement' && (
          <div data-testid="purchases-statement">
            <div className="flex items-center gap-3 mb-4">
              <Label className="text-xs">{t('supplier') || 'Supplier'}:</Label>
              <Select value={stmtSupplierId || ''} onValueChange={setStmtSupplierId}>
                <SelectTrigger className="rounded-none h-9 w-72" data-testid="purchases-stmt-supplier"><SelectValue placeholder={t('selectSupplier') || 'Select supplier'} /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(c => <SelectItem key={c.supplier_id} value={c.supplier_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {statement && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white border border-zinc-200 p-4"><p className="text-xs text-zinc-500">{t('totalInvoiced') || 'Invoiced'}</p><p className="text-lg font-mono font-bold mt-1">{fmtMoney(statement.total_invoiced)}</p></div>
                  <div className="bg-white border border-emerald-200 p-4"><p className="text-xs text-emerald-700">{t('totalPayments') || 'Payments'}</p><p className="text-lg font-mono font-bold text-emerald-700 mt-1">{fmtMoney(statement.total_payments)}</p></div>
                  <div className={`bg-white border p-4 ${statement.balance > 0 ? 'border-red-200' : 'border-emerald-200'}`}><p className="text-xs text-zinc-500">{t('balance') || 'Balance'}</p><p className={`text-lg font-mono font-bold mt-1 ${statement.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(statement.balance)}</p></div>
                </div>
                <div className="bg-white border border-zinc-200">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-2.5">{t('date')}</th>
                      <th className="text-left px-4 py-2.5">{t('reference') || 'Reference'}</th>
                      <th className="text-left px-4 py-2.5">{t('description')}</th>
                      <th className="text-right px-4 py-2.5">{t('charge') || 'Charge'}</th>
                      <th className="text-right px-4 py-2.5">{t('payment')}</th>
                    </tr></thead>
                    <tbody>
                      {statement.entries.map((e, i) => (
                        <tr key={i} className="border-b border-zinc-100">
                          <td className="px-4 py-2 font-mono text-xs">{fmtDate(e.date)}</td>
                          <td className="px-4 py-2 font-mono text-xs">{e.reference}</td>
                          <td className="px-4 py-2 text-zinc-700">{e.description}</td>
                          <td className="px-4 py-2 text-right font-mono text-red-700">{e.type === 'invoice' ? fmtMoney(e.amount) : ''}</td>
                          <td className="px-4 py-2 text-right font-mono text-emerald-700">{e.type === 'payment' ? fmtMoney(e.amount) : ''}</td>
                        </tr>
                      ))}
                      {statement.entries.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-zinc-400">{t('noData') || 'No entries'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!statement && <p className="text-zinc-400 text-sm">{t('selectSupplierToShowStatement') || 'Select a supplier to view their statement.'}</p>}
          </div>
        )}

        {/* CUSTOMER EDIT DIALOG */}
        <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
          <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="purchases-supplier-dialog">
            <DialogHeader><DialogTitle>{editingSupplier ? (t('editSupplier') || 'Edit Supplier') : (t('addSupplier') || 'Add Supplier')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('name') || 'Name'} *</Label>
                <Input
                  value={supplierForm.name}
                  onChange={e => setSupplierForm(f => ({ ...f, name: e.target.value }))}
                  className="rounded-none mt-1"
                  autoFocus
                  data-testid="purchases-supplier-name"
                />
              </div>
              <div><Label>{t('email')}</Label><Input value={supplierForm.email} onChange={e => setSupplierForm(f => ({ ...f, email: e.target.value }))} className="rounded-none mt-1" data-testid="purchases-supplier-email" /></div>
              <div><Label>{t('billingAddress') || 'Billing address'}</Label><Input value={supplierForm.billing_address} onChange={e => setSupplierForm(f => ({ ...f, billing_address: e.target.value }))} className="rounded-none mt-1" data-testid="purchases-supplier-billing" /></div>

              {/* Phase 458 — Opening Balance fields (optional). Stored as
                  absolute amount + debit/credit type so the user UI stays
                  intuitive while the backend normalises to a signed value. */}
              <div className="border border-zinc-200 bg-zinc-50 p-3 space-y-2" data-testid="purchases-supplier-opening-balance">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t('openingBalance') || 'Opening Balance'}</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">{t('amount') || 'Amount'} (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={supplierForm.opening_balance}
                      onChange={e => setSupplierForm(f => ({ ...f, opening_balance: e.target.value }))}
                      placeholder="0.00"
                      className="rounded-none mt-1 font-mono h-9 text-sm"
                      data-testid="purchases-supplier-ob-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('type') || 'Type'}</Label>
                    <Select
                      value={supplierForm.opening_balance_type}
                      onValueChange={v => setSupplierForm(f => ({ ...f, opening_balance_type: v }))}
                    >
                      <SelectTrigger className="rounded-none mt-1 h-9 text-sm" data-testid="purchases-supplier-ob-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="debit">{t('debitOwed') || 'Debit (we owe)'}</SelectItem>
                        <SelectItem value="credit">{t('creditHeld') || 'Credit (held)'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{t('date') || 'Date'}</Label>
                    <Input
                      type="date"
                      value={supplierForm.opening_balance_date}
                      onChange={e => setSupplierForm(f => ({ ...f, opening_balance_date: e.target.value }))}
                      className="rounded-none mt-1 font-mono h-9 text-sm"
                      data-testid="purchases-supplier-ob-date"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-zinc-500">{t('openingBalanceHint') || 'Carry-over from your previous system. Appears as the first row in the supplier statement.'}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setSupplierDialog(false)}>{t('cancel')}</Button>
              <Button className="rounded-none" onClick={saveSupplier} data-testid="purchases-supplier-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SUPPLIER DELETE CONFIRM — Phase 433 */}
        <AlertDialog open={!!supplierDeleteTarget} onOpenChange={(o) => !o && setSupplierDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none max-w-md" data-testid="purchases-supplier-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteSupplier') || 'Delete supplier'}: {supplierDeleteTarget?.name}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('supplierDeleteHint') || 'A supplier with invoices or payments cannot be deleted unless you cascade.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="purchases-supplier-delete-cascade-toggle">
              <input type="checkbox" checked={supplierDeleteCascade} onChange={e => setSupplierDeleteCascade(e.target.checked)} className="mt-0.5" />
              <span><strong>{t('alsoDeleteRecords') || 'Also delete all invoices and payments for this supplier'}</strong></span>
            </label>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction onClick={deleteSupplier} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="purchases-supplier-delete-confirm">{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* PAYMENT DELETE CONFIRM — Phase 433 */}
        <AlertDialog open={!!paymentDeleteTarget} onOpenChange={(o) => !o && setPaymentDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none" data-testid="purchases-pay-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment') || 'Delete payment'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeletePayment') || 'Delete this payment? Allocations on linked invoices will be reversed.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction onClick={deletePayment} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="purchases-pay-delete-confirm">{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* INVOICE DIALOG */}
        <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
          <DialogContent className="rounded-none sm:max-w-[1100px] max-h-[90vh] overflow-y-auto" data-testid="purchases-invoice-dialog">
            <DialogHeader><DialogTitle>{editingInvoice ? (t('editInvoice') || 'Edit Invoice') : (t('newInvoice') || 'New Invoice')}</DialogTitle></DialogHeader>
            <div className="space-y-5">
              {/* Top section — Building / Client / dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">{t('supplier') || 'Supplier'} *</Label>
                  <Select value={invoiceForm.supplier_id} onValueChange={v => setInvoiceForm(f => ({ ...f, supplier_id: v }))}>
                    <SelectTrigger className="rounded-none mt-1 h-9" data-testid="purchases-inv-supplier"><SelectValue placeholder={t('selectSupplier') || 'Select supplier'} /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(c => <SelectItem key={c.supplier_id} value={c.supplier_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* Phase 944 — Invoice type selector: Credit or Cash. */}
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceType') || 'Invoice Type'}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { k: 'credit', el: 'Πιστωτικό', en: 'Credit', color: 'bg-red-700' },
                      { k: 'cash',   el: 'Μετρητοίς', en: 'Cash',   color: 'bg-emerald-700' },
                    ].map(opt => {
                      const on = (invoiceForm.invoice_type || 'credit') === opt.k;
                      return (
                        <button
                          key={opt.k}
                          type="button"
                          onClick={() => setInvoiceForm(f => ({ ...f, invoice_type: opt.k }))}
                          className={`h-9 text-xs border transition-colors ${on ? `${opt.color} text-white border-transparent` : 'border-zinc-300 text-zinc-700 hover:bg-zinc-50'}`}
                          data-testid={`purchases-inv-type-${opt.k}`}
                        >
                          {(language === 'en') ? opt.en : opt.el}
                        </button>
                      );
                    })}
                  </div>
                  {invoiceForm.invoice_type === 'cash' && (
                    <p className="text-[11px] text-emerald-700 mt-1 italic" data-testid="purchases-cash-hint">
                      {language === 'en'
                        ? 'Marked as PAID on save. No payment receipt needed.'
                        : 'Σημειώνεται ως ΠΛΗΡΩΜΕΝΟ κατά την αποθήκευση. Δεν χρειάζεται απόδειξη πληρωμής.'}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceNumber') || 'Invoice #'}</Label>
                  <Input
                    value={invoiceForm.invoice_no}
                    onChange={e => setInvoiceForm(f => ({ ...f, invoice_no: e.target.value }))}
                    placeholder={editingInvoice ? '' : (t('autoGenerated') || 'Auto-generated if left blank')}
                    className="rounded-none mt-1 font-mono h-9"
                    data-testid="purchases-inv-number"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceDate') || t('date')}</Label>
                  <Input type="date" value={invoiceForm.date} onChange={e => setInvoiceForm(f => ({ ...f, date: e.target.value }))} className="rounded-none mt-1 font-mono h-9" data-testid="purchases-inv-date" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('terms') || 'Terms'}</Label>
                  <Select value={invoiceForm.terms || '__none__'} onValueChange={(v) => {
                    const term = v === '__none__' ? '' : v;
                    const days = { net15: 15, net30: 30, net45: 45, net60: 60 }[term];
                    setInvoiceForm(f => {
                      const next = { ...f, terms: term };
                      if (days && f.date) {
                        const dt = new Date(f.date);
                        dt.setDate(dt.getDate() + days);
                        next.due_date = dt.toISOString().slice(0, 10);
                      }
                      return next;
                    });
                  }}>
                    <SelectTrigger className="rounded-none mt-1 h-9" data-testid="purchases-inv-terms"><SelectValue placeholder={t('selectTerms') || 'Select'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('custom') || 'Custom'}</SelectItem>
                      <SelectItem value="dueOnReceipt">{t('dueOnReceipt') || 'Due on Receipt'}</SelectItem>
                      <SelectItem value="net15">Net 15</SelectItem>
                      <SelectItem value="net30">Net 30</SelectItem>
                      <SelectItem value="net45">Net 45</SelectItem>
                      <SelectItem value="net60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('dueDate') || 'Due date'}</Label>
                  <Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm(f => ({ ...f, due_date: e.target.value, terms: '' }))} className="rounded-none mt-1 font-mono h-9" data-testid="purchases-inv-duedate" />
                </div>
              </div>

              {/* Invoice items */}
              <div className="border border-zinc-200" data-testid="purchases-inv-items">
                <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('invoiceItems') || 'Invoice Items'}</span>
                  <button
                    type="button"
                    onClick={() => setInvoiceForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, rate: '', vat_rate: 19 }] }))}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                    data-testid="purchases-inv-add-line"
                  >+ {t('addLine') || 'Add Line'}</button>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50/50">
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-500 font-semibold uppercase tracking-wide">{t('itemDescription') || 'Item Description'}</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[80px]">{t('quantity') || 'Qty'}</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[110px]">{t('rate') || 'Rate'} (€)</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[80px]">{t('vatRate') || 'VAT'} %</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[110px]">{t('amount') || 'Amount'}</th>
                      <th className="w-[36px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoiceForm.items || []).map((it, idx) => {
                      const qty = parseFloat(it.quantity) || 0;
                      const rate = parseFloat(it.rate) || 0;
                      const lineNet = qty * rate;
                      return (
                        <tr key={idx} className="border-b border-zinc-100" data-testid={`purchases-inv-line-${idx}`}>
                          <td className="px-2 py-1.5">
                            <Input value={it.description} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], description: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 border-zinc-200" placeholder={t('itemDescription') || 'Description'} data-testid={`purchases-inv-line-desc-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" min="0" value={it.quantity} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], quantity: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`purchases-inv-line-qty-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" min="0" value={it.rate} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], rate: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`purchases-inv-line-rate-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.5" min="0" max="100" value={it.vat_rate} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], vat_rate: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`purchases-inv-line-vat-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold text-zinc-800" data-testid={`purchases-inv-line-amount-${idx}`}>&euro;{lineNet.toFixed(2)}</td>
                          <td className="px-1 py-1.5 text-center">
                            <button type="button" onClick={() => setInvoiceForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="p-1 text-zinc-400 hover:text-red-500" data-testid={`purchases-inv-line-remove-${idx}`}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Bottom totals — Phase 569: mirrors the backend's
                  rate-group VAT aggregation + ROUND_HALF_UP so the
                  preview value is byte-for-byte identical to what gets
                  persisted and what appears on Allocate/Outstanding/PDF. */}
              {(() => {
                const r2 = (x) => {
                  const n = Number(x) || 0;
                  return Math.round((n + Number.EPSILON) * 100) / 100;
                };
                const items = invoiceForm.items || [];
                const lineNets = items.map(it => r2((parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0)));
                const subtotal = r2(lineNets.reduce((s, n) => s + n, 0));
                const vatByRate = items.reduce((acc, it, i) => {
                  const r = parseFloat(it.vat_rate) || 0;
                  acc[r] = (acc[r] || 0) + lineNets[i];
                  return acc;
                }, {});
                const rawVat = r2(
                  Object.entries(vatByRate).reduce((s, [r, net]) => s + net * (parseFloat(r) || 0) / 100, 0)
                );
                const discount = r2(Math.max(0, parseFloat(invoiceForm.discount) || 0));
                const adjustment = r2(parseFloat(invoiceForm.adjustment) || 0);
                const ratio = subtotal > 0 ? (subtotal - discount) / subtotal : 0;
                const vatTotal = r2(rawVat * ratio);
                const grand = r2((subtotal - discount) + vatTotal + adjustment);
                return (
                  <div className="flex justify-end">
                    <div className="w-full md:w-[380px] border border-zinc-200 bg-zinc-50/50 p-3 space-y-1.5 text-xs" data-testid="purchases-inv-totals">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">{t('subtotal') || 'Subtotal'}</span>
                        <span className="font-mono font-semibold text-zinc-800" data-testid="purchases-inv-subtotal" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-zinc-500 shrink-0">{t('discount') || 'Discount'}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400">&euro;</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={invoiceForm.discount}
                            onChange={e => setInvoiceForm(f => ({ ...f, discount: e.target.value }))}
                            className="rounded-none h-6 w-24 text-right font-mono text-xs border-zinc-300"
                            data-testid="purchases-inv-discount-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">{t('netAfterDiscount') || 'Net after discount'}</span>
                        <span className="font-mono text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{(subtotal - discount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">{t('totalVat') || 'VAT Total'}</span>
                        <span className="font-mono text-zinc-700" data-testid="purchases-inv-vat-total" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{vatTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-zinc-500 shrink-0">{t('adjustment') || 'Adjustment'}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400">&euro;</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={invoiceForm.adjustment}
                            onChange={e => setInvoiceForm(f => ({ ...f, adjustment: e.target.value }))}
                            className="rounded-none h-6 w-24 text-right font-mono text-xs border-zinc-300"
                            data-testid="purchases-inv-adjustment-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-between border-t-2 border-zinc-900 pt-2 mt-2">
                        <span className="font-bold text-zinc-900 uppercase tracking-wider">{t('grandTotal') || 'Grand Total'}</span>
                        <span className="font-mono font-bold text-base text-zinc-900" data-testid="purchases-inv-grand-total" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{grand.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div>
                <Label className="text-xs text-zinc-500">{t('notes') || 'Notes'}</Label>
                <textarea value={invoiceForm.notes || ''} onChange={e => setInvoiceForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full mt-1 border border-zinc-200 rounded-none px-2 py-1.5 text-sm" placeholder={t('optional') || 'Optional'} data-testid="purchases-inv-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setInvoiceDialog(false)}>{t('cancel')}</Button>
              <Button className="rounded-none bg-zinc-950" onClick={saveInvoice} data-testid="purchases-inv-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DELETE INVOICE CONFIRM — Phase 418: Capacitor-safe (no window.confirm) */}
        <AlertDialog open={!!invoiceDeleteTarget} onOpenChange={(o) => !o && setInvoiceDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none" data-testid="purchases-inv-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteInvoice') || 'Delete Invoice'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeleteInvoice') || 'Are you sure you want to delete invoice'} <strong>{invoiceDeleteTarget?.invoice_no}</strong>? {t('thisCannotBeUndone') || 'This action cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="purchases-inv-delete-cancel">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction onClick={() => deleteInvoice(invoiceDeleteTarget)} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="purchases-inv-delete-confirm">{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* SEND-EMAIL DIALOG — Phase 418 */}
        <Dialog open={!!invoiceEmailTarget} onOpenChange={(o) => !o && setInvoiceEmailTarget(null)}>
          <DialogContent className="rounded-none max-w-xl" data-testid="purchases-inv-email-dialog">
            <DialogHeader>
              <DialogTitle>{t('sendByEmail') || 'Send by Email'} — {invoiceEmailTarget?.invoice_no}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t('to') || 'To'} *</Label>
                <Input value={invoiceEmailForm.to} onChange={e => setInvoiceEmailForm(f => ({ ...f, to: e.target.value }))} className="rounded-none mt-1" placeholder="supplier@example.com" data-testid="purchases-inv-email-to" />
              </div>
              <div>
                <Label className="text-xs">CC</Label>
                <Input value={invoiceEmailForm.cc} onChange={e => setInvoiceEmailForm(f => ({ ...f, cc: e.target.value }))} className="rounded-none mt-1" placeholder={t('optional') || 'Optional, comma-separated'} data-testid="purchases-inv-email-cc" />
              </div>
              <div>
                <Label className="text-xs">{t('subject') || 'Subject'}</Label>
                <Input value={invoiceEmailForm.subject} onChange={e => setInvoiceEmailForm(f => ({ ...f, subject: e.target.value }))} className="rounded-none mt-1" data-testid="purchases-inv-email-subject" />
              </div>
              <div>
                <Label className="text-xs">{t('message') || 'Message'}</Label>
                <textarea value={invoiceEmailForm.message} onChange={e => setInvoiceEmailForm(f => ({ ...f, message: e.target.value }))} rows={6} className="w-full mt-1 border border-zinc-200 rounded-none px-2 py-1.5 text-sm" data-testid="purchases-inv-email-message" />
              </div>
              <p className="text-[11px] text-zinc-500">{t('invoicePdfAttached') || 'The invoice PDF will be attached automatically.'}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setInvoiceEmailTarget(null)} disabled={invoiceEmailSending}>{t('cancel') || 'Cancel'}</Button>
              <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800" onClick={sendInvoiceEmail} disabled={invoiceEmailSending} data-testid="purchases-inv-email-send">
                <Envelope size={14} className="mr-1.5" />
                {invoiceEmailSending ? (t('sending') || 'Sending…') : (t('send') || 'Send')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PAYMENT DIALOG */}
        <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
          <DialogContent className="rounded-none max-w-2xl" data-testid="purchases-payment-dialog">
            {(() => {
              const isImported = editingPayment?.source === 'bank_payment';
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      {isImported
                        ? (t('manageImportedPayment') || 'Manage Imported Payment')
                        : editingPayment
                          ? (t('editPayment') || 'Edit Payment')
                          : (t('newPayment') || 'New Payment')}
                      {isImported && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700"
                          data-testid="purchases-pay-imported-badge"
                        >{t('importedFromBankReport') || 'Imported from Bank Report'}</span>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  {isImported && (
                    <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="purchases-pay-imported-banner">
                      {t('importedPaymentLockedFields') || 'Synced from the Bank Statement. Amount, date, bank account and reference are locked. You can update the supplier link, notes and invoice allocations.'}
                    </div>
                  )}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('supplier') || 'Supplier'} *</Label>
                  <Select value={paymentForm.supplier_id} onValueChange={onPaymentClientChange}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="purchases-pay-supplier"><SelectValue placeholder={t('selectSupplier') || 'Select supplier'} /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(c => <SelectItem key={c.supplier_id} value={c.supplier_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('bankAccount') || 'Bank account'}</Label>
                  {isImported ? (
                    <Input
                      value={bankAccounts.find(b => b.account_id === paymentForm.bank_account_id)?.account_name
                        || bankAccounts.find(b => b.account_id === paymentForm.bank_account_id)?.bank_name
                        || '—'}
                      disabled
                      className="rounded-none mt-1 bg-zinc-100"
                      data-testid="purchases-pay-bank-locked"
                    />
                  ) : (
                    <BankAccountCombobox
                      t={t}
                      bankAccounts={bankAccounts}
                      value={paymentForm.bank_account_id}
                      onChange={(id) => setPaymentForm(f => ({ ...f, bank_account_id: id }))}
                      onCreateBank={() => { setPaymentDialog(false); window.location.href = '/finance?tab=banks'; }}
                      testid="purchases-pay-bank"
                    />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t('date')}</Label><Input type="date" value={paymentForm.date} onChange={e => setPaymentForm(f => ({ ...f, date: e.target.value }))} disabled={isImported} className={`rounded-none mt-1 ${isImported ? 'bg-zinc-100' : ''}`} data-testid="purchases-pay-date" /></div>
                <div><Label>{t('amount') || 'Amount'} {!isImported && '*'}</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} disabled={isImported} className={`rounded-none mt-1 ${isImported ? 'bg-zinc-100' : ''}`} data-testid="purchases-pay-amount" /></div>
                <div>
                  <Label>{t('method') || 'Method'}</Label>
                  <Select value={paymentForm.method} onValueChange={v => setPaymentForm(f => ({ ...f, method: v }))} disabled={isImported}>
                    <SelectTrigger className={`rounded-none mt-1 ${isImported ? 'bg-zinc-100' : ''}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('cash') || 'Cash'}</SelectItem>
                      <SelectItem value="bank">{t('bankTransfer') || 'Bank transfer'}</SelectItem>
                      <SelectItem value="card">{t('card') || 'Card'}</SelectItem>
                      <SelectItem value="cheque">{t('cheque') || 'Cheque'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('reference') || 'Reference'}</Label><Input value={paymentForm.reference} onChange={e => setPaymentForm(f => ({ ...f, reference: e.target.value }))} disabled={isImported} className={`rounded-none mt-1 ${isImported ? 'bg-zinc-100' : ''}`} /></div>
                <div><Label>{t('notes') || 'Notes'}</Label><Input value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1" /></div>
              </div>
              {/* Allocations — Phase 452: proper table sorted oldest first */}
              {paymentInvoices.length > 0 && (() => {
                const open = paymentInvoices
                  .filter(i => (i.status || '') !== 'paid' && (Number(i.total || 0) - Number(i.paid_amount || 0)) > 0.005)
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                const allocSum = (paymentForm.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                const amt = Number(paymentForm.amount) || 0;
                const unallocated = Math.max(0, amt - allocSum);
                return (
                  <div className="border border-zinc-200 p-3 bg-zinc-50" data-testid="purchases-pay-allocations">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t('matchPaymentToInvoice') || 'Allocate to invoices'} <span className="text-zinc-400 font-normal">({open.length} {t('openInvoices') || 'open'})</span></p>
                      <p className="text-[11px] text-zinc-500">
                        {t('allocated') || 'Allocated'}: <span className="font-mono font-semibold">€{allocSum.toFixed(2)}</span>
                        {' · '}
                        {t('unallocated') || 'Unallocated'}: <span className={`font-mono font-semibold ${unallocated > 0.005 ? 'text-amber-700' : 'text-emerald-700'}`}>€{unallocated.toFixed(2)}</span>
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto bg-white border border-zinc-100">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold">#</th>
                            <th className="text-left px-2 py-1.5 font-semibold">{t('date') || 'Date'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold">{t('total') || 'Total'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold">{t('outstanding') || 'Outstanding'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold w-32">{t('allocate') || 'Allocate'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {open.map(inv => {
                            const allocated = (paymentForm.allocations || []).find(a => a.invoice_id === inv.invoice_id)?.amount || '';
                            const outstanding = Number(inv.total || 0) - Number(inv.paid_amount || 0);
                            return (
                              <tr key={inv.invoice_id} className="border-t border-zinc-100" data-testid={`purchases-pay-alloc-row-${inv.invoice_id}`}>
                                <td className="px-2 py-1.5 font-mono">{inv.invoice_number || inv.invoice_no || '—'}</td>
                                <td className="px-2 py-1.5 font-mono text-zinc-500">{fmtDate(inv.date)}</td>
                                <td className="px-2 py-1.5 font-mono text-right">€{Number(inv.total || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 font-mono text-right text-red-700 font-semibold">€{outstanding.toFixed(2)}</td>
                                <td className="px-2 py-1">
                                  <div className="flex items-center justify-end gap-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      max={outstanding.toFixed(2)}
                                      min="0"
                                      value={allocated}
                                      onChange={e => setAllocation(inv.invoice_id, e.target.value)}
                                      className="rounded-none h-7 w-24 text-xs text-right font-mono"
                                      placeholder="0.00"
                                      data-testid={`purchases-pay-alloc-${inv.invoice_id}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setAllocation(inv.invoice_id, outstanding.toFixed(2))}
                                      className="text-[9px] text-blue-600 hover:underline whitespace-nowrap"
                                      data-testid={`purchases-pay-alloc-full-${inv.invoice_id}`}
                                    >{t('full') || 'Full'}</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {open.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-4 text-zinc-400">{t('noOpenInvoices') || 'No open invoices to allocate.'}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setPaymentDialog(false)}>{t('cancel')}</Button>
              <Button className="rounded-none" onClick={savePayment} data-testid="purchases-pay-save">{t('save')}</Button>
            </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Container>
  );
}
